import React from 'react';
import {
    COUNTRY_CODES, PRODUCT_TYPES, CAMPAIGN_STATUS,
    CAMPAING_PROMISE_TYPES, FOLLOW_UP_TYPES,
    PANEL_TYPES, CAMPAIGN_PROMISE_EXTRA_TEXTS,
    CAMPAIGN_PROMISE_EXTRA_TEXT_TYPES
} from '../constants/enums';
import dateFunctions from './dateFunctions.js';
import numberFormatter from './numberFormatter';

export default class CampaignValues {

    areObjectsEqual(obj1, obj2) {
        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        if (keys1.length !== keys2.length) {
            return false;
        }

        for (let key of keys1) {
            if (obj1[key] !== obj2[key]) {
                return false;
            }
        }
        return true;
    }

    static isPlayCampaign(product_type_code) {

        if (product_type_code.toLowerCase() === PRODUCT_TYPES.PLAY)
            return true;
        return false;
    };

    static productTypeDescription(countryCode, product_type_code) {

        if (product_type_code !== null && product_type_code.toLowerCase() === PRODUCT_TYPES.PLAY)
            if (countryCode !== null && countryCode.toLowerCase() === COUNTRY_CODES.SWEDEN)
                return "Digital";
            else
                return "Play";
        else
            return "Classic";
    };

    static campaignStatus(startDate, endDate, lp5 = null, hasOutdoorImpactFile = null) {

        const currentDate = new Date();
        const campaignStartDate = new Date(startDate);
        const campaignEndDate = new Date(endDate);

        currentDate.setHours(0, 0, 0, 0);
        campaignStartDate.setHours(0, 0, 0, 0);
        campaignEndDate.setHours(0, 0, 0, 0);

        if (campaignEndDate < currentDate) {
            if (lp5 && lp5.toLowerCase() === CAMPAING_PROMISE_TYPES.SE_CLASSIC_VAC && hasOutdoorImpactFile !== null && !hasOutdoorImpactFile)
                return CAMPAIGN_STATUS.ONGOING;
            return CAMPAIGN_STATUS.COMPLETED;
        }
        else if (campaignStartDate <= currentDate && campaignEndDate >= currentDate)
            return CAMPAIGN_STATUS.ONGOING;
        else
            return CAMPAIGN_STATUS.NOT_STARTED;
    };

    static getTargetValueForClassicWithPanelPromise(targetValue, startDate, endDate) {
        const totalWeeks = dateFunctions.calculateTotalCampaignWeeksNumber(startDate, endDate);
        const weeksPassed = dateFunctions.calculateWeeksPassed(startDate, endDate);

        if (weeksPassed >= totalWeeks) {
            return targetValue;
        }

        const scaledTarget = Math.ceil((totalWeeks / weeksPassed) * targetValue);

        return Math.max(targetValue, scaledTarget);
    }

    static showAddressListVACDeliveredValue(campaignPromise) {
        campaignPromise = campaignPromise.toLowerCase();
        if ([CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_2_0,
            CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_PANELS_2_0,
            CAMPAING_PROMISE_TYPES.DK_PLAY_PLAYS_2_0,
            CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_PANELS_2_0,
            CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_2_0
        ].includes(campaignPromise))
            return true;
        return false;
    }

    static calculateDeliveryPercentageBasedOnPromise(promise, delivered, promiseConfig = null) {
        if (promiseConfig) {
            return this.calculateDeliveryPercentageWithConfig(promise, delivered, promiseConfig);
        }

        const campaignPromise = this.fetchLocalParam5CampaignPromise(promise[0].localParameter5);
        let targetValue = this.fetchTargetBasedOnCampaignPromise(campaignPromise, promise[0], delivered.orderLineDataInformation.idsTarget);
        const deliveredValue = this.fetchResultBasedOnCampaignPromise(campaignPromise, delivered.orderLineDataInformation);
        if (targetValue === undefined || targetValue === null || targetValue === 0)
            return 0;

        if ([
                CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_OI,
                CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS,
                CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_FILLER,
                CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_FILLER,
                CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS,
                CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS,
                CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_2_0,
                CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_VAC_2_0                
            ].includes(campaignPromise)
        ) {
            targetValue = this.getTargetValueForClassicWithPanelPromise(
                targetValue,
                promise[0].periodStart,
                promise[0].periodEnd
            );
        }

        return deliveredValue / targetValue;        
    };

    static calculateDeliveryPercentageWithConfig(promise, delivered, promiseConfig) {
        const { resolvePromiseValues } = require('./promiseFieldResolver');
        const orderLine = promise[0];
        const campaignInfo = delivered.orderLineDataInformation;

        let { target, delivered: deliveredValue, scaleTargetByWeeks, classicNonMomsAllOrNothing } = resolvePromiseValues(
            promiseConfig,
            orderLine,
            campaignInfo,
            campaignInfo.idsTarget
        );

        if (target === undefined || target === null || target === 0)
            return 0;

        if (scaleTargetByWeeks) {
            target = this.getTargetValueForClassicWithPanelPromise(
                target,
                orderLine.periodStart,
                orderLine.periodEnd
            );
        }

        if (classicNonMomsAllOrNothing) {
            const totalPanels = (delivered?.postingData ?? [])
                .reduce((sum, period) => sum + (period.target ?? 0), 0);
            const allPanelsDelivered =
                totalPanels > 0 &&
                campaignInfo.panelPostingsSuccessful === totalPanels &&
                campaignInfo.panelPostingsFailed === 0 &&
                campaignInfo.panelPostingsOngoing === 0;

            return allPanelsDelivered ? 1 : 0;
        }

        return deliveredValue / target;
    };

    static isNonMOMSFollowUpCampaign(campaignPromise) {
        return !!campaignPromise && campaignPromise.toLowerCase() === CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS_METRO_NON_MOMS;
    }


    static fetchCorrectFollowupDataType(campaignPromise, contactsTarget) {
        campaignPromise = campaignPromise.toLowerCase();
        if ([CAMPAING_PROMISE_TYPES.SFI].indexOf(campaignPromise) > -1)
            return FOLLOW_UP_TYPES.PLAYS;
        else if ([CAMPAING_PROMISE_TYPES.VAC].indexOf(campaignPromise) > -1)
            return FOLLOW_UP_TYPES.CONTACTS;
        else if ([CAMPAING_PROMISE_TYPES.SOV, CAMPAING_PROMISE_TYPES.SFI.SOVP].indexOf(campaignPromise) > -1)
            return FOLLOW_UP_TYPES.SCREENS;
        else if (contactsTarget)
            return FOLLOW_UP_TYPES.CONTACTS;

        return FOLLOW_UP_TYPES.PLAYS;
    }

    static fetchLocalParam5CampaignPromise(localParam5){
        let selectedCampaignPromise = '';
        localParam5 = localParam5.toLowerCase();

        const campaignPromises = [CAMPAING_PROMISE_TYPES.SOVP, CAMPAING_PROMISE_TYPES.SOV, CAMPAING_PROMISE_TYPES.SOVD,
            CAMPAING_PROMISE_TYPES.VAC, CAMPAING_PROMISE_TYPES.SFI, CAMPAING_PROMISE_TYPES.FXP,

            CAMPAING_PROMISE_TYPES.SE_DIGITAL_EAVAC_FILLER, CAMPAING_PROMISE_TYPES.SE_CLASSIC_EAVAC,
            CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS, CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_FILLER,
            CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_OI, CAMPAING_PROMISE_TYPES.SE_DIGITAL_EAVAC,
            CAMPAING_PROMISE_TYPES.SE_DIGITAL_PLAYS, CAMPAING_PROMISE_TYPES.SE_DIGITAL_SOV,
            CAMPAING_PROMISE_TYPES.SE_DIGITAL_VAC, CAMPAING_PROMISE_TYPES.SE_DIGITAL_NO_PROMISE,
            CAMPAING_PROMISE_TYPES.SE_CLASSIC_VAC, CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_EAVAC_OI,

            CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS, CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_FILLER,
            CAMPAING_PROMISE_TYPES.NO_PLAY_VAC, CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_FILLER,
            CAMPAING_PROMISE_TYPES.NO_PLAY_PLAYS, CAMPAING_PROMISE_TYPES.NO_CLASSIC_VAC_2_0, 
            CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_2_0, CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_VAC_2_0, 
            CAMPAING_PROMISE_TYPES.NO_CLASSIC_VAC_FILLER_2_0, CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_2_0, 
            CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_FILLER_2_0, CAMPAING_PROMISE_TYPES.NO_PLAY_PLAYS_2_0,
            CAMPAING_PROMISE_TYPES.NO_PLAY_SOV_2_0, CAMPAING_PROMISE_TYPES.NO_AIRPORT_PLAYS_2_0_1,

            CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_PANELS, CAMPAING_PROMISE_TYPES.DK_PLAY_VAC,
            CAMPAING_PROMISE_TYPES.DK_PLAY_PLAYS, CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS,
            CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_PANELS, CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC,
            CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_PANELS_2_0, CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_2_0,
            CAMPAING_PROMISE_TYPES.DK_PLAY_PLAYS_2_0, CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS_2_0, 
            CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS_ADSHEL_2_0,
            CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_PANELS_2_0, CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_2_0,
            CAMPAING_PROMISE_TYPES.DK_DIGITAL_SOV, CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS_METRO_NON_MOMS
        ];

        if (campaignPromises.includes(localParam5))
            return localParam5;

        switch (localParam5) {
            case CAMPAING_PROMISE_TYPES.FXA:
            case CAMPAING_PROMISE_TYPES.FXI:
                selectedCampaignPromise = CAMPAING_PROMISE_TYPES.FLEXAI;
                break;
            default:
                selectedCampaignPromise = '';
                break;
        }
        return selectedCampaignPromise;
    };

    static fetchTargetBasedOnCampaignPromise(campaignPromise, targetData, IDS_Target=0 ) {
        let targetValue = 0;
        switch (campaignPromise.toLowerCase()) {
            case CAMPAING_PROMISE_TYPES.SOVP:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_SOV:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_SOV_2_0:
            case CAMPAING_PROMISE_TYPES.DK_DIGITAL_SOV:
                targetValue = targetData.targetSoV ?? 0.0;
                break;
            case CAMPAING_PROMISE_TYPES.SOV:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_VAC:
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_VAC:
                targetValue = IDS_Target ?? 0;
                break;
            case CAMPAING_PROMISE_TYPES.SOVD:
                targetValue = 0;
                break;
            case CAMPAING_PROMISE_TYPES.PLAY:
            case CAMPAING_PROMISE_TYPES.SFI:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_PLAYS:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_PLAYS:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_PLAYS_2_0:
            case CAMPAING_PROMISE_TYPES.NO_AIRPORT_PLAYS_2_0_1:                
            case CAMPAING_PROMISE_TYPES.DK_PLAY_PLAYS:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_PLAYS_2_0:                            
                targetValue = numberFormatter.roundContactsAsInPopAPI(targetData.targetPlays ?? 0);
                break;
            case CAMPAING_PROMISE_TYPES.VAC:
            case CAMPAING_PROMISE_TYPES.FLEXAI:
            case CAMPAING_PROMISE_TYPES.FXP:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_EAVAC:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_EAVAC_FILLER:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_FILLER:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_2_0:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_FILLER_2_0:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_PANELS_2_0:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_2_0:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_PANELS_2_0:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_2_0:                
                targetValue = targetData.targetVAC ?? 0;
                break;
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_EAVAC:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_VAC_2_0:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_VAC_FILLER_2_0:
            targetValue = targetData.postingTargetVac ?? 0;
                break; 
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_OI:
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS:
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_FILLER:
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_EAVAC_OI:                
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_FILLER:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS_2_0:
            case CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS_ADSHEL_2_0:               
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_2_0:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_VAC_2_0:
                targetValue = targetData.postingTarget ?? 0;
                break;
            default:
                targetValue = 0;
                break;
        }
        return targetValue;
    };

    static fetchResultBasedOnCampaignPromise(campaignPromise, resultData ) {
        let resultValue = 0;
        switch (campaignPromise.toLowerCase()) {
            case CAMPAING_PROMISE_TYPES.SOVP:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_SOV:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_SOV_2_0:
            case CAMPAING_PROMISE_TYPES.DK_DIGITAL_SOV:
                resultValue = resultData.soV ?? 0.0;
                break;
            case CAMPAING_PROMISE_TYPES.SOV:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_VAC:
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_VAC:
                resultValue = resultData.idsVac ?? 0;
                break;
            case CAMPAING_PROMISE_TYPES.SOVD:
                resultValue = 0;
                break;
            case CAMPAING_PROMISE_TYPES.PLAY:
            case CAMPAING_PROMISE_TYPES.SFI:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_PLAYS:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_NO_PROMISE:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_PLAYS:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_PLAYS:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_PLAYS_2_0:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_PLAYS_2_0:
            case CAMPAING_PROMISE_TYPES.NO_AIRPORT_PLAYS_2_0_1:
                resultValue = resultData.plays ?? 0;
                break;
            case CAMPAING_PROMISE_TYPES.VAC:
            case CAMPAING_PROMISE_TYPES.FLEXAI:
            case CAMPAING_PROMISE_TYPES.FXP:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_EAVAC:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_EAVAC_FILLER:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_FILLER:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_2_0:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_FILLER_2_0:                
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_PANELS_2_0:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_2_0:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_PANELS_2_0:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_2_0:                
                resultValue = resultData.vacContacts ?? 0;
                break;
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_EAVAC:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_VAC_2_0:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_VAC_FILLER_2_0:
                resultValue = resultData.podPostingsSuccessfulVAC ?? 0;
                break;
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_OI:
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS:
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_FILLER:
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_EAVAC_OI:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_FILLER:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS_2_0:                
            case CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS_ADSHEL_2_0:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_2_0:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_VAC_2_0:              
                resultValue = resultData.podPanelsDelivered ?? 0;
                break;
            default:
                resultValue = 0;
                break;
        }
        return resultValue;
    };

    static isPromisePercentValue(campaignPromise) {
        return [
            CAMPAING_PROMISE_TYPES.SOVP, 
            CAMPAING_PROMISE_TYPES.SE_DIGITAL_SOV,
            CAMPAING_PROMISE_TYPES.NO_PLAY_SOV_2_0,
            CAMPAING_PROMISE_TYPES.DK_DIGITAL_SOV
        ].includes(campaignPromise);
    }

    static fetchPromiseFollowUpExtensionBasedOnCampaignPromise(campaignPromise) {
        let desc = "";
        switch (campaignPromise.toLowerCase()) {
            case CAMPAING_PROMISE_TYPES.SOVP:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_SOV:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_SOV_2_0:
            case CAMPAING_PROMISE_TYPES.DK_DIGITAL_SOV:
                desc = "%";
                break;
            case CAMPAING_PROMISE_TYPES.SOV:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_VAC:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_FILLER:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_PANELS_2_0:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_2_0:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_PANELS_2_0:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_2_0:                
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_VAC:
                desc = " VAC";
                break;
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_VAC_2_0:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_VAC_FILLER_2_0:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_2_0:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_FILLER_2_0:                
                desc = " VAC/VI";
                break;
            case CAMPAING_PROMISE_TYPES.SOVD:
                desc = " FIX DESC";
                break;
            case CAMPAING_PROMISE_TYPES.PLAY:
            case CAMPAING_PROMISE_TYPES.SFI:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_PLAYS:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_NO_PROMISE:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_PLAYS:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_PLAYS:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_PLAYS_2_0:                
            case CAMPAING_PROMISE_TYPES.NO_PLAY_PLAYS_2_0:
            case CAMPAING_PROMISE_TYPES.NO_AIRPORT_PLAYS_2_0_1:                
                desc = " Plays";
                break;
            case CAMPAING_PROMISE_TYPES.SFI:
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_OI:
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS:
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_FILLER:
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_EAVAC_OI:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_FILLER:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS_2_0:
            case CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS_ADSHEL_2_0:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_2_0:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_VAC_2_0:
                desc = " Panels";
                break;
            case CAMPAING_PROMISE_TYPES.VAC:
            case CAMPAING_PROMISE_TYPES.FLEXAI:
            case CAMPAING_PROMISE_TYPES.FXP:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_EAVAC:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_EAVAC_FILLER:
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_EAVAC:
                desc = " EA-VAC";
                break;
            default:
                desc = "";
                break;
        }
        return desc;
    }

    static fetchPromiseUserFriendlyDescriptionBasedOnCampaignPromise(campaignPromise) {
        let desc = "";
        switch (campaignPromise.toLowerCase()) {
            case CAMPAING_PROMISE_TYPES.SOVP:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_SOV:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_SOV_2_0:
            case CAMPAING_PROMISE_TYPES.DK_DIGITAL_SOV:
                desc = "SOV";
                break;
            case CAMPAING_PROMISE_TYPES.SOV:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_VAC:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_FILLER:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_PANELS_2_0:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_2_0:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_PANELS_2_0:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_2_0:                
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_VAC:
                desc = "VAC";
                break;
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_VAC_2_0:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_VAC_FILLER_2_0:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_2_0:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_FILLER_2_0:                
                desc = "VAC/VI";
                break;
            case CAMPAING_PROMISE_TYPES.SOVD:
                desc = "FIX DESC";
                break;
            case CAMPAING_PROMISE_TYPES.PLAY:
            case CAMPAING_PROMISE_TYPES.SFI:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_PLAYS:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_PLAYS:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_PLAYS:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_PLAYS_2_0:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_PLAYS_2_0:
            case CAMPAING_PROMISE_TYPES.NO_AIRPORT_PLAYS_2_0_1:
                desc = "Plays";
                break;
            case CAMPAING_PROMISE_TYPES.SFI:
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_OI:
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS:
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_FILLER:
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_PANELS_EAVAC_OI:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_FILLER:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS_2_0:
            case CAMPAING_PROMISE_TYPES.DK_CLASSIC_PANELS_ADSHEL_2_0:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_2_0:
            case CAMPAING_PROMISE_TYPES.NO_CLASSIC_PANELS_VAC_2_0:                
                desc = "Panels";
                break;
            case CAMPAING_PROMISE_TYPES.VAC:
            case CAMPAING_PROMISE_TYPES.FLEXAI:
            case CAMPAING_PROMISE_TYPES.FXP:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_EAVAC:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_EAVAC_FILLER:
            case CAMPAING_PROMISE_TYPES.SE_CLASSIC_EAVAC:
                desc = "EA-VAC";
                break;
            default:
                desc = "";
                break;
        }
        return desc;
    }

    static returnPanelTypesBasedOnCreativeMetadata(mediaArray) {

        const instance = new CampaignValues();

        const PLAY_SPECTACULAR_SIZE_1 = { height: "648", width: "2880" };
        const PLAY_SPECTACULAR_SIZE_2 = { height: "864", width: "3968" };
        const PLAY_SPECTACULAR_SIZE_PORTRAIT = { height: "1104", width: "624" };
        const PLAY_SPECTACULAR_SIZE_PORTRAIT_2 = { height: "1656", width: "936" };
        const PLAY_BILLBOARD_SIZE_1 = { height: "576", width: "1152" };
        const PLAY_BILLBOARD_SIZE_2 = { height: "1080", width: "1920" };
        const PLAY_ADSHEL_SIZE = { height: "1920", width: "1080" };
        const PLAY_STORM_SIZE = { height: "990", width: "1800" };
        const CLASSIC_ESCALATOR_SIZE = { height: "700", width: "500" }; 
        const CLASSIC_ADSHELL_3GROUP_SIZE = { height: "1530", width: "1030" };
        const CLASSIC_ADSHEL_SIZE = { height: "1750", width: "1185" };

        const panelTypes = [];

        // We only check for the panel sizes we support in PoD
        mediaArray.forEach(creative => {
            const creative_comparison = { height: creative.height.trim(), width: creative.width.trim() };
            let panelType = "";

            if (instance.areObjectsEqual(creative_comparison, PLAY_ADSHEL_SIZE))
                panelType = PANEL_TYPES.ADSHEL_PLAY;
            //else if (instance.areObjectsEqual(creative_comparison, PLAY_BILLBOARD_SIZE_1))
            //    panelType = PANEL_TYPES.BILLBOARD_PLAY;
            else if (instance.areObjectsEqual(creative_comparison, PLAY_BILLBOARD_SIZE_2))
                panelType = PANEL_TYPES.BILLBOARD_PLAY;
            else if (instance.areObjectsEqual(creative_comparison, PLAY_SPECTACULAR_SIZE_PORTRAIT))
                panelType = PANEL_TYPES.SPECTACULAR_PORTRAIT_PLAY;
            else if (instance.areObjectsEqual(creative_comparison, PLAY_SPECTACULAR_SIZE_PORTRAIT_2))
                panelType = PANEL_TYPES.SPECTACULAR_PORTRAIT_PLAY;
            else if (instance.areObjectsEqual(creative_comparison, PLAY_STORM_SIZE))
                panelType = PANEL_TYPES.STORM_PLAY;
            else if (instance.areObjectsEqual(creative_comparison, CLASSIC_ESCALATOR_SIZE))
                panelType = PANEL_TYPES.ESCALATOR_CLASSIC;
            else if (instance.areObjectsEqual(creative_comparison, CLASSIC_ADSHELL_3GROUP_SIZE))
                panelType = PANEL_TYPES.THREE_GROUP_CLASSIC;
            else if (instance.areObjectsEqual(creative_comparison, CLASSIC_ADSHEL_SIZE))
                panelType = PANEL_TYPES.ADSHEL_CLASSIC;

            if (panelType !== "" && !panelTypes.includes(panelType))
                panelTypes.push(panelType);
        });

        return panelTypes;
    }

    static isSOVCampaing(campaignPromise) {
        if (campaignPromise === CAMPAING_PROMISE_TYPES.SOV)
            return true;
        else if (campaignPromise === CAMPAING_PROMISE_TYPES.SE_DIGITAL_SOV)
            return true;

        return false;
    }

    static caluclateCampaignName(alternativeName, customerName, periodStart, periodEnd) {
        return alternativeName ||
            `${customerName} w.${dateFunctions.calculateCampaignWeeks(
                periodStart,
                periodEnd
            )}`
    }

    // this is more lightweight than introducing another library but if this gets unwieldy
    // consider switching to "dompurify" to sanitize the text before setting the text as
    // <div dangerouslySetInnerHTML={{ __html: sanitizedText }} />;
    static simpleHtmlParser(text) {
        if (!text) return '';

        let parts = [];
        let currentIndex = 0;
        const textLength = text.length;

        while (currentIndex < textLength) {
            const nextTagStart = text.indexOf('<', currentIndex);

            if (nextTagStart === -1) {
                // No more tags, add remaining text
                parts.push(text.slice(currentIndex));
                break;
            }

            if (nextTagStart > currentIndex) {
                parts.push(text.slice(currentIndex, nextTagStart));
            }

            const nextTagEnd = text.indexOf('>', nextTagStart);
            if (nextTagEnd === -1) break; // Malformed HTML

            const tag = text.slice(nextTagStart + 1, nextTagEnd);

            if (tag.startsWith('a ')) {
                // Extract href
                const hrefMatch = tag.match(/href=["'](.*?)["']/);
                if (hrefMatch) {
                    const href = hrefMatch[1];
                    const closingTagStart = text.indexOf('</a>', nextTagEnd);
                    if (closingTagStart !== -1) {
                        const linkText = text.slice(nextTagEnd + 1, closingTagStart);

                        parts.push(
                            <a
                                key={`link-${currentIndex}`}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 underline"
                            >
                                {linkText}
                            </a>
                        );

                        currentIndex = closingTagStart + 4; // Move past </a>
                        continue;
                    }
                }
            } else if (tag === 'br') {
                parts.push(<br key={`br-${currentIndex}`} />);
            } else if (tag === 'strong') {
                const closingTagStart = text.indexOf('</strong>', nextTagEnd);
                if (closingTagStart !== -1) {
                    const strongText = text.slice(nextTagEnd + 1, closingTagStart);
                    parts.push(<strong key={`strong-${currentIndex}`}>{strongText}</strong>);
                    currentIndex = closingTagStart + 9; // Move past </strong>
                    continue;
                }
            } else if (tag === 'em') {
                const closingTagStart = text.indexOf('</em>', nextTagEnd);
                if (closingTagStart !== -1) {
                    const emText = text.slice(nextTagEnd + 1, closingTagStart);
                    parts.push(<em key={`em-${currentIndex}`}>{emText}</em>);
                    currentIndex = closingTagStart + 5; // Move past </em>
                    continue;
                }
            }

            currentIndex = nextTagEnd + 1;
        }

        return parts;
    }

    static fetchEtxraTextForCampaignPromise(campaignPromise, textType = null, hasUdda = false) {
        if (!campaignPromise) {
            return '';
        }

        campaignPromise = campaignPromise.toLowerCase();
        let matchingTexts = [];

        Object.entries(CAMPAIGN_PROMISE_EXTRA_TEXTS || {}).forEach(([groupKey, group]) => {
            if (group && Array.isArray(group.types)) {
                const types = group.types.map(type =>
                    typeof type === 'string' ? type.toLowerCase() : ''
                );

                if (types.includes(campaignPromise)) {
                    let text = group.text || '';

                    if (hasUdda) {
                        if (groupKey.startsWith('SE')) {
                            text += '<br><br> EA-VAC numbers are calculated based on a full week';
                        } else {
                            text += '<br><br> VAC numbers are calculated based on a full week';
                        }
                    }

                    matchingTexts.push({
                        key: groupKey,
                        text: text,
                        type: group.textType
                    });
                }
            }
        });

        // If no matches found
        if (matchingTexts.length === 0) {
            //console.log('no matches found');
            return '';
        }

        // If only one match found and no textType sent in
        if (matchingTexts.length === 1 && textType === null) {
            //console.log('One matche found');
            return this.simpleHtmlParser(matchingTexts[0].text);
        }

        // If textType is specified, find the matching text
        if (textType) {
            //console.log('TextType specified');
            const specificText = matchingTexts.find(t => t.type === textType);
            if (specificText) {
                //console.log('TextType specificText found');
                return this.simpleHtmlParser(specificText.text);
            }
        }

        // If no text type matched or none provided return empty string
        return '';
    }
}

